import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import Input from './ui/Input';
import TextArea from './ui/TextArea';
import Select from './ui/Select';
import Button from './ui/Button';
import SvgReportWizard from './SvgReportWizard';

const useSvgForm = true;

function NewTicketModal({ setIsOpen, onReportCreated }) {
        const { user, profile } = useAuth();
    const [formData, setFormData] = useState({
        organization_id: '',
        title: '',
        wizardRoom: '',
        wizardDescription: '',
        severity: 'medium'
    });
    const [organizations, setOrganizations] = useState([]);
    const [wizardCompleted, setWizardCompleted] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchOrganizations = async () => {
            if (!profile?.organization_ids || profile.organization_ids.length === 0) {
                setError('You must be assigned to an organization to create tickets. Please contact your administrator.');
                return;
            }

            try {
                const { data, error: fetchError } = await supabase
                    .from('organizations')
                    .select('id, name')
                    .in('id', profile.organization_ids);

                if (fetchError) {
                    throw fetchError;
                }

                setOrganizations(data || []);

                if (data && data.length === 1) {
                    setFormData(prev => ({
                        ...prev,
                        organization_id: data[0].id
                    }));
                }
            } catch (err) {
                setError('Failed to load organizations. Please try again.');
                console.error('Error fetching organizations:', err);
            }
        };

        fetchOrganizations();
    }, [profile]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
        setError(''); // Clear error when user modifies form
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);

        if (files.length > 5) {
            setError('Maximum 5 images allowed');
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                setError(`File ${file.name} exceeds 5MB limit`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        setSelectedFiles(validFiles);

        const previews = validFiles.map(file => URL.createObjectURL(file));
        setFilePreviews(previews);
    };

    const uploadImages = async (reportId) => {
        if (selectedFiles.length === 0) return [];

        try {
            const uploadedUrls = [];

            for (const file of selectedFiles) {
                const fileExt = file.name.split('.').pop();
                const fileName = `${reportId}/${Date.now()}_${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
                const filePath = `${user.id}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('damage-reports')
                    .upload(filePath, file);

                if (uploadError) {
                    throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
                }

                const { data: { publicUrl } } = supabase.storage
                    .from('damage-reports')
                    .getPublicUrl(filePath);

                uploadedUrls.push(publicUrl);
            }

            return uploadedUrls;
        } catch (err) {
            throw new Error(`Image upload failed: ${err.message}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title) {
            setError('Title is required');
            return;
        }

        if (!formData.organization_id) {
            setError('Organization is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { data: reportData, error: insertError } = await supabase
                .from('reports')
                .insert([{
                    tenant_id: user.id,
                    tenant_name: user.email || 'Unknown',
                    organization_id: formData.organization_id,
                    unit: null,
                    title: formData.title,
                    description: formData.wizardRoom && formData.wizardDescription
                        ? `${formData.wizardRoom}\n\n${formData.wizardDescription}`
                        : formData.wizardDescription || '',
                    severity: formData.severity,
                    status: 'open',
                    image_urls: []
                }])
                .select();

            if (insertError) {
                throw new Error(`Failed to create report: ${insertError.message}`);
            }

            const reportId = reportData[0].id;

            let imageUrls = [];
            if (selectedFiles.length > 0) {
                imageUrls = await uploadImages(reportId);

                const { error: updateError } = await supabase
                    .from('reports')
                    .update({ image_urls: imageUrls })
                    .eq('id', reportId);

                if (updateError) {
                    // Non-critical error
                }
            }

            if (onReportCreated) {
                onReportCreated();
            }

            setIsOpen(false);
        } catch (err) {
            setError(err.message || 'Failed to create report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setFilePreviews(prev => {
            URL.revokeObjectURL(prev[index]);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleWizardComplete = (wizardData) => {
        setFormData(prev => ({
            ...prev,
            wizardRoom: `${wizardData.room}/${wizardData.item}`,
            wizardDescription: wizardData.description
        }));
        setWizardCompleted(true);
    };

    return (
        <Modal isOpen={true} onClose={() => setIsOpen(false)} size="lg">
            <Modal.Header onClose={() => setIsOpen(false)}>
                <Modal.Title>New Maintenance Report</Modal.Title>
            </Modal.Header>

            <form onSubmit={handleSubmit}>
                <Modal.Content>
                    {error && (
                        <div className="bg-error-50 text-error-700 p-3 rounded-lg text-sm mb-4">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        {organizations.length > 1 && (
                            <Select
                                label="Organization *"
                                id="organization_id"
                                value={formData.organization_id}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            >
                                <option value="">Select an organization</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Select>
                        )}

                        <Input
                            label="Title *"
                            id="title"
                            placeholder="Brief title for the issue"
                            type="text"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                        />

                        {useSvgForm ? (
                                                                                                    <div className={`py-12 ${wizardCompleted ? 'border-2 border-success-500 rounded-lg p-4' : ''}`}>
                                                                                                        {wizardCompleted ? (                                    <div>
                                        <h3 className='text-lg font-semibold text-success-700'>Issue Selected:</h3>
                                        <p className='text-secondary-800 font-medium'>{formData.wizardRoom}</p>
                                        <p className='text-secondary-600'>{formData.wizardDescription}</p>
                                        <Button variant="outline" size="sm" onClick={() => setWizardCompleted(false)} className="mt-2">Change</Button>
                                    </div>
                                ) : (
                                    <SvgReportWizard onComplete={handleWizardComplete} />
                                )}
                            </div>
                        ) : (
                            <>
                                <TextArea
                                    label="Damage Description *"
                                    id="wizardDescription"
                                    placeholder="Please describe the damage in detail..."
                                    rows={4}
                                    value={formData.wizardDescription}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                />
                            </>
                        )}

                        <Select
                            label="Severity Level"
                            id="severity"
                            value={formData.severity}
                            onChange={handleInputChange}
                            disabled={loading}
                        >
                            <option value="low">Low - Can wait</option>
                            <option value="medium">Medium - Normal priority</option>
                            <option value="high">High - Urgent</option>
                            <option value="urgent">Urgent - Safety concern</option>
                        </Select>

                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-secondary-700">
                                Add Photos (Optional)
                            </label>
                            <p className="text-xs text-secondary-500">
                                Max 5 images, 5MB each
                            </p>
                            <input
                                id="images"
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={loading}
                                className="w-full px-4 py-3 border border-secondary-300 rounded-lg transition-smooth focus-ring bg-white"
                            />
                        </div>

                        {filePreviews.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-semibold text-secondary-700">
                                    Selected Images ({filePreviews.length})
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {filePreviews.map((preview, index) => (
                                        <div key={index} className="relative aspect-square bg-secondary-200 rounded-lg overflow-hidden">
                                            <img
                                                src={preview}
                                                alt={`Preview ${index + 1}`}
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-error text-white border-none cursor-pointer flex items-center justify-center text-base font-bold hover:bg-error-600 transition-smooth"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        variant="secondary"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        loading={loading}
                    >
                        Submit Report
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

export default NewTicketModal;
